import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, ArrowRight } from 'lucide-react';
import { useBuyerAuth } from '../contexts/BuyerAuthContext';
import { trackEvent } from '../utils/analytics';
import './DealerCTA.css';

/**
 * "Register as a dealer" band.
 *
 * Dealers buy repeatedly and in volume, and sales works them completely
 * differently from someone buying one taxi to drive. The registration form asks
 * who you are buying for, but nobody reaches that form on purpose — this is the
 * thing that sends them there, and it is why `buyers.buyer_type` gets filled in
 * at all.
 *
 * It routes by what we already know about the visitor rather than dumping
 * everyone on the same page:
 *   • signed out          → OTP login, carrying the dealer intent through
 *   • signed in, not a dealer → the profile form with dealer pre-selected
 *   • already a dealer    → nothing at all; pitching them again is noise
 *
 * The "dealer pricing" promise is fulfilled by the sales team off the back of
 * the lead — there is no dealer price in the database, and this component must
 * not imply the site will show one.
 */
const DealerCTA = ({ variant = 'band' }) => {
  const { buyer, token } = useBuyerAuth();

  // Already told us they are a dealer — they have the thing this is selling.
  if (buyer?.buyer_type === 'dealer') return null;

  const signedIn = !!token;
  const to = signedIn ? '/buyer/register?type=dealer' : '/buyer/login?type=dealer';

  return (
    <section className={`dealer-cta dealer-cta-${variant}`} data-testid="dealer-cta">
      <div className="dealer-cta-inner">
        <div className="dealer-cta-icon" aria-hidden="true">
          <BadgeCheck size={26} />
        </div>
        <div className="dealer-cta-copy">
          <h2>Buying for a dealership or fleet?</h2>
          <p>
            Register as a dealer and our team works your requirement directly — first look at
            fresh stock before it goes live, pricing on multiple units, and someone who calls you
            when the vehicles you deal in come in.
          </p>
          <ul className="dealer-cta-points">
            <li>First look at stock before it is listed</li>
            <li>Negotiated pricing on multiple units</li>
            <li>Alerts for the categories you actually trade in</li>
          </ul>
        </div>
        <Link
          to={to}
          className="dealer-cta-btn"
          data-testid="dealer-cta-btn"
          onClick={() => trackEvent('dealer_cta_click', { signedIn, variant })}
        >
          Register as a Dealer <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  );
};

export default DealerCTA;
